// src/utils/exportUtils.js
import * as XLSX from "xlsx";
import dayjs from "@/plugins/dayjs";
import {
    getParentIdsWithChildren,
    getUmbrellaOdooIds,
} from "@/utils/projectUtils";

export const ODOO_EXPORT_COLUMNS = [
    "date",
    "unit_amount",
    "project_id/.id",
    "description",
];

const LUNCH_BREAK_PROJECT_ID = "SYSTEM_LUNCH-BREAK";

/**
 * One Odoo description segment.
 * When includeDuration (amassed row with 2+ entries): with text →
 * `text (Xh:Ymin)` / `Name: text (…)` / `SUPLI: text (…)` / `SUPLI Name: text (…)`.
 * Without text, single-project segments are omitted (""); shared Odoo id keeps
 * `Name: (Xh:Ymin)`; OT fallback without text is `SUPLI (Xh:Ymin)` or
 * `SUPLI Name (Xh:Ymin)` (no colon).
 * When duration is omitted (one entry in the row, or separateOdooEntries):
 * `text` / `Name: text` / `SUPLI: text` / `SUPLI` / `SUPLI Name` — hours live in
 * unit_amount only.
 *
 * @param {string} projectName
 * @param {string} description
 * @param {number} durationMs
 * @param {{ prefixSupli?: boolean, includeDuration?: boolean, includeProjectName?: boolean }} [options]
 * @returns {string}
 */
export function formatOdooExportDescriptionLine(
    projectName,
    description,
    durationMs,
    {
        prefixSupli = false,
        includeDuration = true,
        includeProjectName = false,
    } = {}
) {
    const text = description && String(description).trim();
    let head = "";
    if (includeProjectName) {
        const name = (projectName && String(projectName).trim()) || "Unknown";
        head = prefixSupli ? `SUPLI ${name}` : name;
    } else if (prefixSupli) {
        head = "SUPLI";
    }

    if (!includeDuration) {
        if (head && text) return `${head}: ${text}`;
        if (head) return head;
        return text || "";
    }

    const mins = durationMs / 60000;
    const h = Math.floor(mins / 60);
    const m = Math.round(mins % 60);
    const label = h ? `${h}h:${m}min` : `${m}min`;

    if (text) {
        if (head) return `${head}: ${text} (${label})`;
        return `${text} (${label})`;
    }

    // No work text: omit bare times for a single-project row; keep times when
    // the Odoo id is shared across projects, or when tagging OT fallback SUPLI.
    if (includeProjectName) {
        return prefixSupli ? `${head} (${label})` : `${head}: (${label})`;
    }
    if (prefixSupli) return `SUPLI (${label})`;
    return "";
}

/**
 * Round decimal hours up to the next quarter hour:
 * 0 → .00; (0,.25] → .25; (.25,.50] → .50; (.50,.75] → .75; (.75,1) → next integer.
 *
 * @param {number} hours
 * @returns {number}
 */
export function roundHoursToQuarter(hours) {
    if (!Number.isFinite(hours)) return 0;
    const sign = hours < 0 ? -1 : 1;
    const abs = Math.abs(hours);
    const whole = Math.floor(abs + 1e-9);
    const cents = Math.round((abs - whole) * 100);

    if (cents === 0) return sign * whole;
    if (cents <= 25) return sign * (whole + 0.25);
    if (cents <= 50) return sign * (whole + 0.5);
    if (cents <= 75) return sign * (whole + 0.75);
    return sign * (whole + 1);
}

/**
 * Trim/coerce an Odoo project id for export.
 * Empty / whitespace-only values become "" so callers can skip the entry:
 * Odoo timesheet import needs a real project_id/.id and will error (or
 * partially fail) on blank ids rather than ignoring those rows.
 */
function normalizeOdooId(value) {
    if (value === null || value === undefined) return "";
    return String(value).trim();
}

/**
 * Resolve which Odoo project id to export for an entry.
 * Regular → odooId. Overtime → odooIdSupli when set; otherwise fall back to
 * odooId and mark appendSupli so the description gets a SUPLI tag.
 * Empty odooId means the entry should be omitted from the CSV.
 *
 * @param {Object} meta
 * @param {boolean} isOvertime
 * @returns {{ odooId: string, appendSupli: boolean }}
 */
export function resolveOdooExportId(meta, isOvertime) {
    if (!meta) return { odooId: "", appendSupli: false };
    if (!isOvertime) {
        return {
            odooId: normalizeOdooId(meta.odooId),
            appendSupli: false,
        };
    }
    const supli = normalizeOdooId(meta.odooIdSupli);
    if (supli) return { odooId: supli, appendSupli: false };
    const odooId = normalizeOdooId(meta.odooId);
    return { odooId, appendSupli: Boolean(odooId) };
}

/**
 * Build Odoo timesheet CSV rows from completed time entries.
 * Default: one row per calendar day + resolved Odoo id (+ OT-without-Supli bucket).
 * Projects with separateOdooEntries: one CSV row per time entry (no duration in description).
 * Overtime uses odooIdSupli when set; otherwise odooId with a SUPLI description prefix.
 * Project name is added when the export Odoo id is in an umbrella group
 * (parent/child via parentProjectId, or legacy duplicate odooId), or when
 * multiple app projects land in the same row — except when the row only has
 * the parent project (Odoo already shows that name via project_id). Per-segment
 * times appear only when the row amasses 2+ entries. Amassed segments without
 * description are omitted unless the Odoo id is in an umbrella or the segment
 * is OT-fallback SUPLI. Segments join with ` | `.
 *
 * @param {Array<Object>} timeEntries - Entries with projectId, startTime, endTime, description, overtime
 * @param {Array<Object>} projects - Projects with projectId, odooId, odooIdSupli, name, separateOdooEntries, parentProjectId
 * @returns {Array<{ date: string, unit_amount: number, "project_id/.id": string, description: string }>}
 */
export function buildOdooExportRows(timeEntries = [], projects = []) {
    const projectMeta = {};
    (projects || []).forEach((project) => {
        if (project?.projectId) {
            projectMeta[project.projectId] = {
                odooId: project.odooId,
                odooIdSupli: project.odooIdSupli,
                name: project.name || "",
                separateOdooEntries: project.separateOdooEntries === true,
                parentProjectId: project.parentProjectId || null,
            };
        }
    });
    const umbrellaOdooIds = getUmbrellaOdooIds(projects);
    const parentIdsWithChildren = getParentIdsWithChildren(projects);

    const pending = [];

    (timeEntries || []).forEach((entry, index) => {
        const {
            projectId,
            startTime,
            endTime,
            description,
            overtime,
            id,
        } = entry || {};
        if (!projectId || projectId === LUNCH_BREAK_PROJECT_ID) return;
        if (!startTime || !endTime) return;

        const start = Number(startTime);
        const end = Number(endTime);
        if (isNaN(start) || isNaN(end) || end <= start) return;

        const meta = projectMeta[projectId];
        const isOvertime = overtime === true;
        const { odooId, appendSupli } = resolveOdooExportId(meta, isOvertime);
        if (!odooId) return;

        pending.push({
            projectId,
            projectName: meta.name,
            odooId,
            appendSupli,
            separateOdooEntries: meta.separateOdooEntries,
            entryKey: id || `${start}-${index}`,
            start,
            end,
            description: description || "",
        });
    });

    const buckets = {};

    pending.forEach((p) => {
        const date = dayjs(p.start).format("YYYY-MM-DD");
        // Separate-entries projects: one row per time entry.
        // Otherwise: one row per day + odoo id; OT fallback (SUPLI) stays
        // separate from regular even when both use the same normal odooId.
        const key = p.separateOdooEntries
            ? `${date}|${p.odooId}|${p.appendSupli ? "supli" : "reg"}|${p.projectId}|${p.entryKey}`
            : `${date}|${p.odooId}|${p.appendSupli ? "supli" : "reg"}`;
        if (!buckets[key]) {
            buckets[key] = {
                date,
                odooId: p.odooId,
                appendSupli: p.appendSupli,
                separateOdooEntries: p.separateOdooEntries,
                totalMs: 0,
                projectIds: new Set(),
                segments: [],
            };
        }
        const durationMs = p.end - p.start;
        buckets[key].totalMs += durationMs;
        buckets[key].projectIds.add(p.projectId);
        buckets[key].segments.push({
            projectName: p.projectName,
            description: p.description,
            durationMs,
            appendSupli: p.appendSupli,
        });
    });

    return Object.values(buckets)
        .filter((b) => b.totalMs > 0)
        .sort(
            (a, b) =>
                a.date.localeCompare(b.date) ||
                a.odooId.localeCompare(b.odooId) ||
                Number(a.appendSupli) - Number(b.appendSupli)
        )
        .map((b) => {
            // Umbrella / multi-project rows get Pulse names, but a row that
            // only contains the parent omits the name — Odoo already shows it.
            const onlyProjectId =
                b.projectIds.size === 1 ? [...b.projectIds][0] : null;
            const onlyParentPresent =
                Boolean(onlyProjectId) &&
                parentIdsWithChildren.has(onlyProjectId) &&
                !projectMeta[onlyProjectId]?.parentProjectId;
            const includeProjectName =
                (b.projectIds.size > 1 || umbrellaOdooIds.has(b.odooId)) &&
                !onlyParentPresent;
            // Times in description only when multiple entries were amassed into
            // this row; otherwise unit_amount already carries the (rounded) hours.
            const includeDuration =
                !b.separateOdooEntries && b.segments.length > 1;
            const descriptions = b.segments
                .map((s) =>
                    formatOdooExportDescriptionLine(
                        s.projectName,
                        s.description,
                        s.durationMs,
                        {
                            prefixSupli: s.appendSupli,
                            includeDuration,
                            includeProjectName,
                        }
                    )
                )
                .filter(Boolean);
            return {
                date: b.date,
                unit_amount: roundHoursToQuarter(b.totalMs / 3600000),
                "project_id/.id": b.odooId,
                description: descriptions.join(" | "),
            };
        });
}

/**
 * Export Odoo timesheet rows as CSV with fixed column order.
 *
 * @param {Array<Object>} rows
 * @param {Object} options
 * @param {string} [options.filename='odoo-timesheet.csv']
 * @param {boolean} [options.download=true]
 * @returns {{ csv: string, blob: Blob }}
 */
export function exportOdooCsv(rows, options = {}) {
    const { filename = "odoo-timesheet.csv", download = true } = options;
    return exportStatisticsCsv(rows, {
        filename,
        columns: ODOO_EXPORT_COLUMNS,
        download,
    });
}

/**
 * Escape a value for CSV (quotes, commas, newlines).
 *
 * @param {*} value
 * @returns {string}
 */
export function escapeCsvValue(value) {
    if (value === null || value === undefined) return "";
    const str = String(value);
    if (/[",\n\r]/.test(str)) {
        return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
}

/**
 * Convert an array of plain objects to a CSV string.
 * Column order is taken from `columns` when provided; otherwise from the first row keys.
 *
 * @param {Array<Object>} rows
 * @param {string[]|null} columns
 * @returns {string}
 */
export function toCsv(rows = [], columns = null) {
    if (!rows.length && (!columns || !columns.length)) {
        return "";
    }
    const cols =
        columns && columns.length
            ? columns
            : Object.keys(rows[0] || {});
    const header = cols.map(escapeCsvValue).join(",");
    const body = rows
        .map((row) => cols.map((c) => escapeCsvValue(row[c])).join(","))
        .join("\n");
    return body ? `${header}\n${body}` : header;
}

/**
 * Trigger a browser download for a Blob.
 *
 * @param {string} filename
 * @param {Blob} blob
 */
export function downloadBlob(filename, blob) {
    if (typeof document === "undefined") return;
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

/**
 * Build a CSV Blob (and optionally download).
 *
 * @param {Array<Object>} rows
 * @param {Object} options
 * @param {string} [options.filename='statistics.csv']
 * @param {string[]|null} [options.columns]
 * @param {boolean} [options.download=true]
 * @returns {{ csv: string, blob: Blob }}
 */
export function exportStatisticsCsv(rows, options = {}) {
    const {
        filename = "statistics.csv",
        columns = null,
        download = true,
    } = options;
    const csv = toCsv(rows, columns);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    if (download) {
        downloadBlob(filename, blob);
    }
    return { csv, blob };
}

/**
 * Build an XLSX workbook from named sheets of row objects.
 *
 * @param {Array<{ name: string, rows: Array<Object> }>} sheets
 * @returns {XLSX.WorkBook}
 */
export function buildWorkbook(sheets = []) {
    const wb = XLSX.utils.book_new();
    sheets.forEach(({ name, rows }) => {
        const safeName = (name || "Sheet").slice(0, 31);
        const ws = XLSX.utils.json_to_sheet(rows || []);
        XLSX.utils.book_append_sheet(wb, ws, safeName);
    });
    return wb;
}

/**
 * Export statistics to Excel (multiple sheets).
 *
 * @param {Array<{ name: string, rows: Array<Object> }>} sheets
 * @param {Object} options
 * @param {string} [options.filename='statistics.xlsx']
 * @param {boolean} [options.download=true]
 * @returns {{ workbook: XLSX.WorkBook, arrayBuffer: ArrayBuffer }}
 */
export function exportStatisticsXlsx(sheets, options = {}) {
    const { filename = "statistics.xlsx", download = true } = options;
    const workbook = buildWorkbook(sheets);
    const arrayBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
    });
    if (download) {
        const blob = new Blob([arrayBuffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        downloadBlob(filename, blob);
    }
    return { workbook, arrayBuffer };
}
